import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { createAuthenticatedClient } from '@/lib/graphql';
import { useAuth } from '@/lib/auth/AuthContext';

// Types
export interface PromptConfig {
  id: string;
  configId: string;
  bedrockPromptName: string;
  createdAt: string;
  updatedAt: string;
}

export interface UpdatePromptConfigInput {
  bedrockPromptName: string;
}

// Queries
const GET_PROMPT_CONFIG = gql`
  query GetPromptTemplate {
    promptTemplate {
      id
      configId
      bedrockPromptName
      createdAt
      updatedAt
    }
  }
`;

// Mutations
const UPDATE_PROMPT_CONFIG = gql`
  mutation UpdatePromptTemplate($input: UpdatePromptConfigInput!) {
    updatePromptTemplate(input: $input) {
      promptTemplate {
        id
        configId
        bedrockPromptName
        createdAt
        updatedAt
      }
    }
  }
`;

// Hooks
export function usePromptTemplate() {
  const { accessToken } = useAuth();

  return useQuery({
    queryKey: ['promptTemplate'],
    queryFn: async () => {
      if (!accessToken) throw new Error('Not authenticated');
      const client = createAuthenticatedClient(accessToken);
      const data = await client.request<{ promptTemplate: PromptConfig }>(
        GET_PROMPT_CONFIG
      );
      return data.promptTemplate;
    },
    enabled: !!accessToken,
  });
}

export function useUpdatePromptTemplate() {
  const { accessToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdatePromptConfigInput) => {
      if (!accessToken) throw new Error('Not authenticated');
      const client = createAuthenticatedClient(accessToken);
      const data = await client.request<{
        updatePromptTemplate: {
          promptTemplate: PromptConfig;
        };
      }>(UPDATE_PROMPT_CONFIG, { input });
      return data.updatePromptTemplate.promptTemplate;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promptTemplate'] });
    },
  });
}
